// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import { kjua } from 'kjua';
import { KuromiEngine } from "./kuromi-engine.js";
import { BunnyBrowserDevice } from './browser-device.js';
import {
	type DevicePage,
	type FirstBootMenu,
	type RuntimeMenu,
} from './components.js';

export class BrowserKuromiEngine extends KuromiEngine {

	private firstMenu = document.createElement('first-boot-menu') as FirstBootMenu;
	private deviceRegistration = document.createElement('device-page') as DevicePage;
	private runtimeMenu = document.createElement('runtime-menu') as RuntimeMenu;

	constructor(private params: {
		userCodeElement: Element,
		verificationUriElement: Element,
		qrContainerElement: Element,
	}) {
		super();
		this.firstMenu.addEventListener('menu-action', (event) => {
			const { action } = event.detail;
			// Handle menu action
			if(action === 'register-device') {
				document.body.removeChild(this.firstMenu);
				this._changeState(KuromiEngine.DEVICE_REGISTRATION);
			}
		});
		this.runtimeMenu.addEventListener('menu-action', (event) => {
			const { action } = event.detail;
			this.runtimeMenu.innerHTML = '';
			// Handle menu action
			if(action === 'device-registration') {
				this.runtimeMenu.appendChild(this.deviceRegistration);
			}
		});
		this.runtimeMenu.addEventListener('menu-hide', () => {
			document.body.removeChild(this.runtimeMenu);
			this._changeState(KuromiEngine.PLAYLIST_RUNNING);
		});
	}

	start(options: {
		issuer_url: string,
	}): void {
		console.log("BrowserKuromiEngine started");
		window.dsbunny.rmm.device = new BunnyBrowserDevice({ issuer_url: options.issuer_url });
		console.log('BunnyBrowserDevice created');
		window.dsbunny.rmm.addEventListener('usercodechange', (_event: Event) => {
			if(typeof window.dsbunny.rmm.user_code === 'undefined') {
				throw new Error('`user_code` is undefined');
			}
			this.params.userCodeElement.textContent = window.dsbunny.rmm.user_code;
		});
		window.dsbunny.rmm.addEventListener('verificationurichange', (_event: Event) => {
			if(typeof window.dsbunny.rmm.verification_uri === 'undefined') {
				throw new Error('`verification_uri` is undefined');
			}
			this.params.verificationUriElement.textContent = window.dsbunny.rmm.verification_uri;
		});
		window.dsbunny.rmm.addEventListener('verificationuricompletechange', (_event: Event) => {
			if(typeof window.dsbunny.rmm.verification_uri_complete === 'undefined') {
				throw new Error('`verification_uri_complete` is undefined');
			}
			this.params.qrContainerElement.innerHTML = '';
			const qr = kjua({
				text: window.dsbunny.rmm.verification_uri_complete,
				ecLevel: 'L',
				size: 400,
				rounded: 100,
				quiet: 1,
			});
			this.params.qrContainerElement.appendChild(qr);
		});
		window.dsbunny.rmm.addEventListener('statechange', (_event: Event) => {
			if(window.dsbunny.rmm.state === 'authorized') {
				this._changeState(KuromiEngine.PLAYLIST_RUNNING);
			} else if(window.dsbunny.rmm.state === 'authorizing') {
				this._changeState(KuromiEngine.DEVICE_REGISTRATION);
			} else if(window.dsbunny.rmm.state === 'revoked') {
				this._changeState(KuromiEngine.DEVICE_REVOKED);
			} else if(window.dsbunny.rmm.state === 'error') {
				this._changeState(KuromiEngine.ENGINE_ERROR);
			}
		});

		const deviceId = window.dsbunny.rmm.device.getDeviceId();
		if(deviceId) {
			console.log(`Device ID found: ${deviceId}`);
			this._changeState(KuromiEngine.PLAYLIST_RUNNING);
		} else {
			this._changeState(KuromiEngine.FIRST_BOOT_MENU);
		}
	}

	private _changeState(newState: string) {
		if(this.state === newState) {
			return;
		}
		this.state = newState;
		console.log(`State changed to ${this.state}`);
		if(this.state === KuromiEngine.FIRST_BOOT_MENU) {
			this._firstBootMenu();
		} else if(this.state === KuromiEngine.DEVICE_REGISTRATION) {
			this._deviceRegistration();
		} else if(this.state === KuromiEngine.DEVICE_REVOKED) {
			this._deviceRevoked();
		} else if(this.state === KuromiEngine.PLAYLIST_RUNNING) {
			this._playlistRunning();
		} else if(this.state === KuromiEngine.RUNTIME_MENU) {
			this._runtimeMenu();
		} else if(this.state === KuromiEngine.ENGINE_ERROR) {
			this._engineError();
		}
	}

	private _onKeyDown(event: KeyboardEvent) {
		if(event.key === 'Escape') {
			this._removeKeyListener();
			this._runtimeMenu();
		}
	}
	private _onKeyDownBound = this._onKeyDown.bind(this);

	private _removeKeyListener() {
		document.removeEventListener('keydown', this._onKeyDownBound);
	}

	private _addKeyListener() {
		document.addEventListener('keydown', this._onKeyDownBound);
	}

	private _deviceRegistration() {
		document.body.className = 'activation';
		window.dsbunny.rmm.dispatchEvent(new Event('statechange'));
		window.dsbunny.rmm.device!.poll();
	}

	private _deviceRevoked() {
		document.body.className = 'revoked';
	}

	private _playlistRunning() {
		document.body.className = 'activated';
		window.dsbunny.rmm.dispatchEvent(new Event('statechange'));
		window.dsbunny.rmm.device!.poll();
		this._addKeyListener();
	}

	// TBD: pausing the playlist when the menu is shown and unhooking
	// event handlers, specifically `keydown` events that could
	// interfere with menu operation.
	// For now, the playlist continues to run in the background.
	private _firstBootMenu() {
		document.body.appendChild(this.firstMenu);
		this.firstMenu.showMenu();
	}

	private _runtimeMenu() {
		document.body.appendChild(this.runtimeMenu);
		this.runtimeMenu.showMenu();
	}

	private _engineError() {
		document.body.className = 'error';
		const errorCode = document.body.querySelector('#error_code');
		if(errorCode && window.dsbunny.rmm.error_code) {
			errorCode.textContent = window.dsbunny.rmm.error_code;
		}
	}
}
